// Travel Document Service for Firestore Operations
import {
    collection, doc, setDoc, updateDoc, deleteDoc, getDocs,
    serverTimestamp, Timestamp, onSnapshot,
} from 'firebase/firestore';
import { db } from '../config/firebase';

export type TravelDocumentType = 'passport' | 'driving_license' | 'visa' | 'id_card';

export interface TravelDocument {
    id: string;
    userId: string;
    title?: string;
    documentType: TravelDocumentType;
    documentNumber: string;
    issuingCountry: string;
    nationality?: string;
    firstName: string;
    lastName: string;
    expiryDate: Date;
    issueDate?: Date;
    notes?: string;
    createdAt: Date;
    updatedAt: Date;
}

export class TravelDocumentService {
    private static getCollectionPath(userId: string) {
        return `users/${userId}/travel_documents`;
    }

    private static convertTimestamps(data: any): any {
        const converted = { ...data };
        if (data.expiryDate instanceof Timestamp) converted.expiryDate = data.expiryDate.toDate();
        if (data.issueDate instanceof Timestamp) converted.issueDate = data.issueDate.toDate();
        if (data.createdAt instanceof Timestamp) converted.createdAt = data.createdAt.toDate();
        if (data.updatedAt instanceof Timestamp) converted.updatedAt = data.updatedAt.toDate();
        return converted;
    }

    static subscribeToDocuments(userId: string, callback: (docs: TravelDocument[]) => void): () => void {
        const docsRef = collection(db, this.getCollectionPath(userId));
        return onSnapshot(docsRef, (snapshot) => {
            const docs = snapshot.docs.map((d) => ({
                id: d.id,
                ...this.convertTimestamps(d.data()),
            } as TravelDocument));
            docs.sort((a, b) => new Date(a.expiryDate).getTime() - new Date(b.expiryDate).getTime());
            callback(docs);
        }, (error) => {
            console.error('Error in travel documents subscription:', error);
        });
    }

    static async createDocument(
        userId: string,
        document: Omit<TravelDocument, 'id' | 'userId' | 'createdAt' | 'updatedAt'>
    ): Promise<string> {
        const docRef = doc(collection(db, this.getCollectionPath(userId)));
        await setDoc(docRef, {
            ...document,
            userId,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        });
        return docRef.id;
    }

    static async updateDocument(
        userId: string,
        documentId: string,
        updates: Partial<TravelDocument>
    ): Promise<void> {
        await updateDoc(doc(db, this.getCollectionPath(userId), documentId), {
            ...updates,
            updatedAt: serverTimestamp(),
        });
    }

    static async deleteDocument(userId: string, documentId: string): Promise<void> {
        await deleteDoc(doc(db, this.getCollectionPath(userId), documentId));
    }

    /** One-time fetch of all travel documents for a user. */
    static async getDocuments(userId: string): Promise<TravelDocument[]> {
        const snapshot = await getDocs(collection(db, this.getCollectionPath(userId)));
        return snapshot.docs.map(d => ({
            id: d.id,
            ...this.convertTimestamps(d.data()),
        } as TravelDocument));
    }

    static getDocTypeLabel(type: TravelDocumentType): string {
        switch (type) {
            case 'passport': return 'Passport';
            case 'driving_license': return 'Driving License';
            case 'visa': return 'Visa';
            case 'id_card': return 'ID Card';
        }
    }

    static getDocTypeIcon(type: TravelDocumentType): string {
        switch (type) {
            case 'passport': return '🛂';
            case 'driving_license': return '🪪';
            case 'visa': return '📋';
            case 'id_card': return '🆔';
        }
    }

    static isExpiringSoon(expiryDate: Date, monthsBefore: number = 6): boolean {
        const threshold = new Date();
        threshold.setMonth(threshold.getMonth() + monthsBefore);
        return new Date(expiryDate) <= threshold;
    }

    static isExpired(expiryDate: Date): boolean {
        return new Date(expiryDate) < new Date();
    }

    /**
     * Encrypt a document number using AES-256-GCM (Web Crypto API).
     * Key is derived from the user's UID via PBKDF2 — encrypted in the browser
     * before the value is ever sent to Firestore.
     * Encrypted values are prefixed with "enc:" for easy detection.
     */
    static async encryptDocNumber(plainText: string, userId: string): Promise<string> {
        try {
            const encoder = new TextEncoder();
            const salt = encoder.encode('TravelEZ_DocNum_Salt_v1');
            const keyMaterial = await crypto.subtle.importKey(
                'raw',
                encoder.encode(userId),
                { name: 'PBKDF2' },
                false,
                ['deriveKey']
            );
            const key = await crypto.subtle.deriveKey(
                { name: 'PBKDF2', salt, iterations: 100_000, hash: 'SHA-256' },
                keyMaterial,
                { name: 'AES-GCM', length: 256 },
                false,
                ['encrypt']
            );
            const iv = crypto.getRandomValues(new Uint8Array(12));
            const encrypted = await crypto.subtle.encrypt(
                { name: 'AES-GCM', iv },
                key,
                encoder.encode(plainText)
            );
            const combined = new Uint8Array(iv.length + encrypted.byteLength);
            combined.set(iv);
            combined.set(new Uint8Array(encrypted), iv.length);
            return `enc:${btoa(String.fromCharCode(...combined))}`;
        } catch {
            return plainText; // fallback: store plain if crypto unavailable
        }
    }

    /**
     * Decrypt a document number. Returns the plain text.
     * Values not prefixed with "enc:" are treated as legacy plain text.
     */
    static async decryptDocNumber(value: string, userId: string): Promise<string> {
        if (!value || !value.startsWith('enc:')) return value; // legacy plain text
        try {
            const encoder = new TextEncoder();
            const salt = encoder.encode('TravelEZ_DocNum_Salt_v1');
            const combined = Uint8Array.from(atob(value.slice(4)), c => c.charCodeAt(0));
            const iv = combined.slice(0, 12);
            const encrypted = combined.slice(12);
            const keyMaterial = await crypto.subtle.importKey(
                'raw',
                encoder.encode(userId),
                { name: 'PBKDF2' },
                false,
                ['deriveKey']
            );
            const key = await crypto.subtle.deriveKey(
                { name: 'PBKDF2', salt, iterations: 100_000, hash: 'SHA-256' },
                keyMaterial,
                { name: 'AES-GCM', length: 256 },
                false,
                ['decrypt']
            );
            const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, encrypted);
            return new TextDecoder().decode(decrypted);
        } catch {
            return value.replace(/^enc:/, ''); // best-effort fallback
        }
    }

    /** Show last 4 digits only for list display (e.g. "••••1234") */
    static maskDocNumber(docNumber: string): string {
        if (!docNumber) return '••••••••';
        const clean = docNumber.replace(/\s/g, '');
        if (clean.length <= 4) return '••••';
        return '••••' + clean.slice(-4);
    }
}

export default TravelDocumentService;
